import React, { useEffect, useRef } from "react";
import { Card, CardContent } from "./card";
import { Badge } from "./badge";
import { Icon } from "@iconify/react";
import ApexCharts from "apexcharts";
import BarChart from "./barChart";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  chartData?: number[];
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  trend,
  chartData = [30, 40, 35, 50, 49, 60, 70, 91, 125],
}) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const options = {
      series: [
        {
          data: chartData,
        },
      ],
      chart: {
        type: "bar",
        height: 100,
        sparkline: {
          enabled: true,
        },
        toolbar: {
          show: false,
        },
        animations: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 2,
          columnWidth: "60%",
          distributed: true,
          dataLabels: {
            position: "top",
          },
        },
      },
      colors: ["#3B82F6"],
      grid: {
        show: false,
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
      },
      xaxis: {
        crosshairs: {
          width: 1,
        },
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        show: false,
      },
      tooltip: {
        theme: "dark",
        x: {
          show: false,
        },
        y: {
          title: {
            formatter: () => "",
          },
        },
        marker: {
          show: false,
        },
      },
    };

    const chart = new ApexCharts(chartRef.current, options);
    chart.render();

    return () => {
      chart.destroy();
    };
  }, [chartData]);

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon icon={icon} className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-muted-foreground mt-3 mb-2 text-[14px]">
              {title}
            </p>
            <div className="flex items-center gap-2">
              <h3 className="text-2xl font-bold text-foreground">{value}</h3>
              {trend && (
                <Badge
                  className={`${
                    trend.isPositive
                      ? "bg-green-500/10 text-green-400 max-w-[100px]"
                      : "bg-red-500/10 text-red-400 max-w-[100px]"
                  } text-[10px] rounded-[5px] px-[5px] mt-4 max-w-[100px]`}
                >
                  <Icon
                    icon={
                      trend.isPositive
                        ? "ri:arrow-up-line"
                        : "ri:arrow-down-line"
                    }
                    className="w-3 h-3 mr-1"
                  />
                  {Math.abs(trend.value).toFixed(2)}%
                </Badge>
              )}
            </div>
          </div>
          {/* <div className="w-24">
            <div ref={chartRef} />
          </div> */}
          <div className="w-24 sm:w-28 flex-shrink-0 overflow-visible">
            <BarChart />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StatCard;
